const {
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithPopup,
  signOut
} = require('firebase/auth');
const {
  auth,
  db,
  startImpersonation,
  stopImpersonation
} = require('./firebase');
const {
  clearRouteCache,
  clearSiteCache,
  createCacheWebhook,
  loadSite,
  loadRouteEvents,
  loadRouteEventPage,
  loadRouteTimeline,
  loadRouteCacheInfo,
  loadRouteScreenshotSettings,
  loadRouteScreenshot,
  loadSiteSpendEstimate,
  loadSitesPage,
  loadSiteStats,
  provisionDomain,
  removeDomain,
  removeSite,
  revokeCacheWebhook,
  saveSite,
  subscribeToHomepageCache,
  subscribeToSite,
  subscribeToSiteUsage,
  testDomain,
  updateRouteParameter,
  routeRendererSettings,
  updateRouteScreenshotVisibility,
  updatePreviewSlug
} = require('./sites');
const {
  createImpersonationToken,
  createFreshAccountPreview,
  deleteFreshAccountPreview,
  grantAccountDomainTrial,
  loadAccountBilling,
  updateAccountFreeTier
} = require('./admin');
const { acceptAccountTerms, loadTermsAcceptance } = require('./account');
const { loadBillingAccess } = require('./billing');
const {
  addCapture,
  addIgnoredRoute,
  emptySiteForm,
  removeIgnoredRoute,
  siteForm,
  updateSiteForm
} = require('./site-form');
const { navigate } = require('../routes');
const { slugifyPreviewName } = require('../../../shared/preview-slug');
const { validateTargetOrigin } = require('../../../shared/origin-schema');
const { cacheVersionForPath } = require('../../../shared/cache-version');

function createAppController(requestRedraw) {
  let user = null;
  let activeDb = db;
  let impersonationSession = null;
  let impersonatedAccount = null;
  let freshPreviewSession = null;
  let freshPreviewAdminUser = null;
  let freshPreviewAccountId = null;
  let freshAccountPreview = false;
  let startingFreshAccountPreview = false;
  let stoppingFreshAccountPreview = false;
  let isAdmin = false;
  let primaryIsAdmin = false;
  let authResolved = false;
  let termsAccepted = null;
  let termsVersion = null;
  let acceptingTerms = false;
  let sites = [];
  let sitePageCursors = [null];
  let sitePageIndex = 0;
  let sitePageHasNext = false;
  let siteListLoading = false;
  let siteListRequest = 0;
  const ownedSiteLoads = new Set();
  const adminSites = new Map();
  let error = null;
  let form = emptySiteForm();
  let stats = null;
  let statsSiteId = null;
  let statsLoading = false;
  let stopStatsUsageSubscription = null;
  let stopSiteSubscription = null;
  let stopHomepageCacheSubscription = null;
  let statsUsageSiteId = null;
  let statsUsagePeriodHours = 7 * 24;
  let statsRequestVersion = 0;
  let saving = false;
  const spendEstimates = new Map();
  let removingSiteId = null;
  const provisioningDomains = new Set();
  const removingDomains = new Set();
  const cacheWebhookUrls = new Map();

  function domainKey(siteId, domain) {
    return `${siteId}:${domain.toLowerCase()}`;
  }

  async function provisionSiteDomain(siteId, domain) {
    const key = domainKey(siteId, domain);
    provisioningDomains.add(key);
    requestRedraw();

    try {
      await provisionDomain(user, siteId, domain);
      await refreshSites();
    } finally {
      provisioningDomains.delete(key);
      requestRedraw();
    }
  }

  function showError(reason) {
    error = reason?.message || String(reason);
    requestRedraw();
  }

  function dismissError() {
    error = null;
    requestRedraw();
  }

  async function refreshSites(resetPagination = false) {
    if (resetPagination) {
      sitePageCursors = [null];
      sitePageIndex = 0;
    }

    const requestId = ++siteListRequest;
    siteListLoading = true;
    requestRedraw();
    try {
      const page = await loadSitesPage(activeDb, user, sitePageCursors[sitePageIndex]);
      if (requestId !== siteListRequest) return;
      sites = page.sites;
      sitePageHasNext = page.hasNextPage;
      if (page.cursor) sitePageCursors[sitePageIndex + 1] = page.cursor;
    } finally {
      if (requestId === siteListRequest) {
        siteListLoading = false;
        requestRedraw();
      }
    }
  }

  async function changeSitePage(pageIndex) {
    if (siteListLoading || pageIndex < 0 || pageIndex === sitePageIndex) return;
    if (pageIndex > sitePageIndex && !sitePageHasNext) return;
    if (pageIndex > sitePageIndex + 1 ||
      (pageIndex > 0 && !sitePageCursors[pageIndex])) return;
    sitePageIndex = pageIndex;
    await refreshSites();
  }

  async function ensureOwnedSite(siteId) {
    if (!siteId || !user || ownedSiteLoads.has(siteId)) return;
    const requestedUser = user;
    const requestedDb = activeDb;
    ownedSiteLoads.add(siteId);
    try {
      const site = await loadSite(requestedDb, siteId);
      if (site && user?.uid === requestedUser?.uid && activeDb === requestedDb &&
        site.ownerId === requestedUser?.uid) {
        sites = [...sites.filter(candidate => candidate.id !== site.id), site];
        requestRedraw();
      }
    } catch (reason) {
      showError(reason);
    } finally {
      ownedSiteLoads.delete(siteId);
    }
  }

  async function ensureAdminSite(siteId) {
    if (!isAdmin || adminSites.has(siteId)) {
      return;
    }

    try {
      const site = await loadSite(activeDb, siteId);
      if (site) {
        adminSites.set(siteId, site);
      }
    } catch (reason) {
      showError(reason);
      return;
    }

    requestRedraw();
  }

  async function signIn(redirectPath) {
    error = null;

    try {
      await signInWithPopup(auth, new GoogleAuthProvider());
      if (typeof redirectPath === 'string') {
        navigate(redirectPath);
      }
    } catch (reason) {
      showError(reason);
    }
  }

  async function acceptTerms() {
    if (!user || !termsVersion) {
      return;
    }

    acceptingTerms = true;
    error = null;
    requestRedraw();

    try {
      await acceptAccountTerms(user, termsVersion);
      termsAccepted = true;
      await refreshSites();
    } catch (reason) {
      showError(reason);
    } finally {
      acceptingTerms = false;
      requestRedraw();
    }
  }

  async function save(event) {
    event.preventDefault();
    error = null;

    try {
      form.origin = validateTargetOrigin(form.origin);
    } catch (reason) {
      showError(reason);
      return;
    }

    saving = true;
    requestRedraw();

    try {
      const isNewSite = !form.id;
      const savedSite = sites.find(site => site.id === form.id);
      const savedPreviewSlug = savedSite?.previewSlug || slugifyPreviewName(savedSite?.name);
      const requestedPreviewSlug = String(form.previewSlug || '').trim();
      const previewSlugChanged = !isNewSite &&
        requestedPreviewSlug &&
        requestedPreviewSlug !== savedPreviewSlug;

      if (previewSlugChanged) {
        try {
          await updatePreviewSlug(user, form.id, form.previewSlug);
        } catch (reason) {
          throw new Error(`Unable to update the preview subdomain: ${reason.message}`);
        }
      }
      let siteId;
      try {
        const previousIgnoredRoutes = savedSite?.renderOptions?.ignoredRoutes || [];
        const nextIgnoredRoutes = form.ignoredRoutes.map(route => route.trim()).filter(Boolean);
        const ignoreRoutesChanged = previousIgnoredRoutes.length !== nextIgnoredRoutes.length ||
          previousIgnoredRoutes.some((route, index) => route !== nextIgnoredRoutes[index]);
        const rendererChanged = (savedSite?.renderOptions?.renderer || 'chromium') !== form.renderer;
        siteId = (await saveSite(activeDb, user, form, ignoreRoutesChanged || rendererChanged)).siteId;
      } catch (reason) {
        throw new Error(`Unable to save the site: ${reason.message}`);
      }
      if (isNewSite) {
        try {
          await updatePreviewSlug(user, siteId, form.previewSlug || slugifyPreviewName(form.name));
        } catch (reason) {
          throw new Error(`Site created, but the preview subdomain could not be saved: ${reason.message}`);
        }
      }
      await refreshSites();

      if (isNewSite) {
        form = { ...form, id: siteId };
        navigate(`/sites/${siteId}`);
      }
    } catch (reason) {
      showError(reason);
    } finally {
      saving = false;
      requestRedraw();
    }
  }

  async function addDomain(site) {
    const domain = form.domain.trim();

    if (!domain) {
      showError(new Error('Enter a public domain before adding it'));
      return;
    }

    if (site.domains?.includes(domain)) {
      showError(new Error(`${domain} is already connected to this site`));
      return;
    }

    error = null;
    form.domain = '';
    requestRedraw();

    try {
      await provisionSiteDomain(site.id, domain);
    } catch (reason) {
      showError(reason);
    }
  }

  async function refreshDomain(site, domain) {
    try {
      await provisionSiteDomain(site.id, domain);
    } catch (reason) {
      showError(reason);
    }
  }

  async function testConfiguredDomain(site, domain) {
    return testDomain(user, site.id, domain);
  }

  async function updateRouteSettings(site, path, parameter, action) {
    try {
      const result = await updateRouteParameter(user, site.id, path, parameter, action);
      requestRedraw();
      return result;
    } catch (reason) {
      console.error(`Unable to update route settings for ${site.id}${path}.`, reason);
      showError(reason);
      throw reason;
    }
  }

  async function updateRouteScreenshot(site, path, publicScreenshot) {
    try {
      const result = await updateRouteScreenshotVisibility(user, site.id, path, publicScreenshot);
      requestRedraw();
      return result;
    } catch (reason) {
      console.error(`Unable to update screenshot visibility for ${site.id}${path}.`, reason);
      showError(reason);
      throw reason;
    }
  }

  async function updateRouteRenderer(site, path, renderer) {
    try {
      const result = await routeRendererSettings(user, site.id, path, renderer);
      await refreshSites();
      requestRedraw();
      return result;
    } catch (reason) {
      showError(reason);
      throw reason;
    }
  }

  async function removeConfiguredDomain(site, domain) {
    if (!window.confirm(`Remove ${domain} from ${site.name}? It will stop being served by Spache.`)) {
      return;
    }

    const key = domainKey(site.id, domain);
    removingDomains.add(key);
    requestRedraw();

    try {
      await removeDomain(user, site.id, domain);
      await refreshSites();
    } catch (reason) {
      showError(reason);
    } finally {
      removingDomains.delete(key);
      requestRedraw();
    }
  }

  async function clearCache(site) {
    if (!window.confirm(`Clear the render cache for ${site.name}?`)) {
      return;
    }

    try {
      await clearSiteCache(activeDb, site.id);
      await refreshSites();
    } catch (reason) {
      showError(reason);
    }
  }

  async function clearRoute(site, path) {
    if (!window.confirm(`Clear the render cache for ${path}?`)) {
      return;
    }

    try {
      await clearRouteCache(user, site.id, path);
      requestRedraw();
    } catch (reason) {
      showError(reason);
    }
  }

  async function createSiteCacheWebhook(site) {
    try {
      const result = await createCacheWebhook(user, site.id);
      cacheWebhookUrls.set(site.id, result.webhookUrl);
      requestRedraw();
    } catch (reason) {
      showError(reason);
    }
  }

  async function revokeSiteCacheWebhook(site) {
    try {
      await revokeCacheWebhook(user, site.id);
      cacheWebhookUrls.delete(site.id);
      requestRedraw();
    } catch (reason) {
      showError(reason);
    }
  }

  async function removeConfiguredSite(site) {
    const confirmed = window.confirm(
      `Remove ${site.name}? This stops Spache serving the site immediately.`
    );

    if (!confirmed) {
      return;
    }

    removingSiteId = site.id;
    requestRedraw();

    try {
      await removeSite(activeDb, site.id);
      form = emptySiteForm();
      await refreshSites();
      navigate('/sites');
    } catch (reason) {
      showError(reason);
    } finally {
      removingSiteId = null;
      requestRedraw();
    }
  }

  async function ensureStats(site, includeSpend = false) {
    if (!site || statsLoading ||
      (statsSiteId === site.id && statsUsageSiteId === site.id &&
        stats?.usagePeriodHours === statsUsagePeriodHours &&
        (!includeSpend || stats.estimatedSpend))) {
      return;
    }

    stopStatsUsageSubscription?.();
    stopStatsUsageSubscription = null;
    stopSiteSubscription?.();
    stopSiteSubscription = null;
    stopHomepageCacheSubscription?.();
    stopHomepageCacheSubscription = null;
    statsUsageSiteId = null;
    statsLoading = true;
    const requestVersion = statsRequestVersion;
    // Never carry the previous site's estimate into this site's view while
    // its own estimate is loading. The estimate contains site-specific usage.
    let estimatedSpend = statsSiteId === site.id ? stats?.estimatedSpend : undefined;
    const spendPromise = includeSpend ? spendEstimate(site)
      .then(estimate => {
        estimatedSpend = estimate;
        if (statsSiteId === site.id && stats) {
          stats = { ...stats, estimatedSpend: estimate };
          requestRedraw();
        }
      })
      .catch(reason => {
        console.error(`Unable to load usage estimate for site ${site.id}.`, reason);
        showError(reason);
      }) : Promise.resolve();

    try {
      const siteStats = await loadSiteStats(activeDb, site, statsUsagePeriodHours);

      if (requestVersion !== statsRequestVersion) {
        return;
      }

      stats = { ...siteStats, estimatedSpend };
      statsSiteId = site.id;
      statsUsageSiteId = site.id;
      let liveSite = site;
      stopSiteSubscription = subscribeToSite(
        activeDb,
        site,
        updatedSite => {
          if (statsSiteId !== site.id) {
            return;
          }

          const siteIndex = sites.findIndex(candidate => candidate.id === site.id);
          if (siteIndex >= 0) {
            sites = sites.map((candidate, index) => index === siteIndex ? updatedSite : candidate);
          }
          if (adminSites.has(site.id)) {
            adminSites.set(site.id, updatedSite);
          }
          if (cacheVersionForPath(updatedSite, '/') !== cacheVersionForPath(liveSite, '/')) {
            liveSite = updatedSite;
            stopHomepageCacheSubscription?.();
            stopHomepageCacheSubscription = subscribeToHomepageCache(
              activeDb,
              updatedSite,
              homepageCached => {
                if (statsSiteId === site.id && stats) {
                  stats = { ...stats, homepageCached };
                  requestRedraw();
                }
              },
              showError
            );
          }
          liveSite = updatedSite;
          requestRedraw();
        },
        showError
      );
      stopHomepageCacheSubscription = subscribeToHomepageCache(
        activeDb,
        site,
        homepageCached => {
          if (statsSiteId === site.id && stats) {
            stats = { ...stats, homepageCached };
            requestRedraw();
          }
        },
        showError
      );
      stopStatsUsageSubscription = subscribeToSiteUsage(
        activeDb,
        site,
        statsUsagePeriodHours,
        usage => {
          if (statsSiteId !== site.id) {
            return;
          }

          stats = { ...stats, ...usage };
          requestRedraw();
        },
        showError
      );
    } catch (reason) {
      showError(reason);
    } finally {
      statsLoading = false;
      requestRedraw();

      if (requestVersion !== statsRequestVersion ||
        statsUsagePeriodHours !== stats?.usagePeriodHours) {
        ensureStats(site);
      }
    }

    await spendPromise;
  }

  function setStatsUsagePeriod(site, periodHours) {
    if (statsUsagePeriodHours === periodHours) {
      return;
    }

    statsUsagePeriodHours = periodHours;
    statsRequestVersion++;
    ensureStats(site);
    requestRedraw();
  }

  function spendEstimate(site) {
    const existing = spendEstimates.get(site.id);

    if (existing?.value) {
      return Promise.resolve(existing.value);
    }

    if (existing?.promise) {
      return existing.promise;
    }

    const promise = loadSiteSpendEstimate(user, site.id)
      .then(estimate => {
        spendEstimates.set(site.id, { value: estimate });
        return estimate;
      })
      .catch(error => {
        spendEstimates.delete(site.id);
        throw error;
      });

    spendEstimates.set(site.id, { promise });
    return promise;
  }

  async function routeCacheInfo(site, path) {
    const [cacheInfo, screenshotSettings, rendererSettings] = await Promise.all([
      loadRouteCacheInfo(activeDb, site, path),
      loadRouteScreenshotSettings(user, site.id, path),
      routeRendererSettings(user, site.id, path)
    ]);
    return { ...(cacheInfo || {}), ...screenshotSettings, ...rendererSettings };
  }

  function routeScreenshot(site, path) {
    return loadRouteScreenshot(user, site.id, path);
  }

  function prepareRoute(route) {
    let site = sites.find(candidate => candidate.id === route.siteId);

    if (!site && route.siteId && !route.name.startsWith('admin-')) {
      ensureOwnedSite(route.siteId);
    }

    if (!site && (route.name === 'admin-site' || route.name === 'admin-stats' || route.name === 'admin-cost')) {
      site = adminSites.get(route.siteId);
      ensureAdminSite(route.siteId);
    }

    if (route.name === 'new-site' && form.id) {
      form = emptySiteForm();
    }

    if (route.name === 'new-site' && route.origin) {
      form.origin = route.origin;
    }

    if (route.name === 'settings' && site && form.id !== site.id) {
      form = siteForm(site);
    }

    if (route.name === 'site' || route.name === 'stats' || route.name === 'cost' ||
      route.name === 'route-settings' ||
      route.name === 'admin-site' || route.name === 'admin-stats' || route.name === 'admin-cost') {
      ensureStats(site, route.name === 'cost' || route.name === 'admin-cost');
    } else if (stopStatsUsageSubscription || stopSiteSubscription || stopHomepageCacheSubscription) {
      stopStatsUsageSubscription?.();
      stopStatsUsageSubscription = null;
      stopSiteSubscription?.();
      stopSiteSubscription = null;
      stopHomepageCacheSubscription?.();
      stopHomepageCacheSubscription = null;
      statsUsageSiteId = null;
    }

    return site;
  }

  function changeForm(name, captureIndex) {
    return event => {
      updateSiteForm(form, name, captureIndex, event.target.value);
      requestRedraw();
    };
  }

  function addFormCapture() {
    addCapture(form);
    requestRedraw();
  }

  function addFormIgnoredRoute() {
    addIgnoredRoute(form);
    requestRedraw();
  }

  function removeFormIgnoredRoute(index) {
    removeIgnoredRoute(form, index);
    requestRedraw();
  }

  function newSite() {
    form = emptySiteForm();
  }

  async function startFreshAccountPreview() {
    if (!primaryIsAdmin || !user || freshAccountPreview || impersonationSession ||
      startingFreshAccountPreview || stoppingFreshAccountPreview) return;

    startingFreshAccountPreview = true;
    const administrator = user;
    error = null;
    requestRedraw();

    try {
      const preview = await createFreshAccountPreview(administrator);
      try {
        freshPreviewSession = await startImpersonation(preview.token);
      } catch (reason) {
        await deleteFreshAccountPreview(administrator, preview.account.uid).catch(cleanupError => {
          console.error('Unable to clean up a temporary account after sign-in failed.', cleanupError);
        });
        throw reason;
      }

      freshPreviewAdminUser = administrator;
      freshPreviewAccountId = preview.account.uid;
      activeDb = freshPreviewSession.db;
      user = freshPreviewSession.user;
      freshAccountPreview = true;
      isAdmin = false;
      stopStatsUsageSubscription?.();
      stopStatsUsageSubscription = null;
      stopSiteSubscription?.();
      stopSiteSubscription = null;
      stopHomepageCacheSubscription?.();
      stopHomepageCacheSubscription = null;
      sites = [];
      sitePageCursors = [null];
      sitePageIndex = 0;
      sitePageHasNext = false;
      siteListRequest++;
      statsRequestVersion++;
      form = emptySiteForm();
      stats = null;
      statsSiteId = null;
      spendEstimates.clear();

      const terms = await loadTermsAcceptance(user);
      termsAccepted = Boolean(
        terms.agreedToTermsAt && terms.termsVersion === terms.currentTermsVersion
      );
      termsVersion = terms.currentTermsVersion;
      if (termsAccepted) await refreshSites();
      navigate('/sites');
    } catch (reason) {
      if (freshAccountPreview) await stopFreshAccountPreview();
      showError(reason);
    } finally {
      startingFreshAccountPreview = false;
      requestRedraw();
    }
  }

  async function stopFreshAccountPreview() {
    if (!freshAccountPreview || stoppingFreshAccountPreview) return;

    stoppingFreshAccountPreview = true;
    const administrator = freshPreviewAdminUser;
    const previewAccountId = freshPreviewAccountId;
    error = null;
    requestRedraw();

    try {
      if (administrator && previewAccountId) {
        await deleteFreshAccountPreview(administrator, previewAccountId);
      }
    } catch (reason) {
      // Leave the admin in control even if immediate cleanup fails; the
      // scheduled expiry cleanup retries abandoned or failed deletions.
      console.error('Temporary account cleanup was deferred to expiry cleanup.', reason);
      showError(new Error('Preview ended, but cleanup did not finish. It will be retried automatically.'));
    } finally {
      if (freshPreviewSession) {
        await stopImpersonation(freshPreviewSession).catch(reason => {
          console.error('Unable to close temporary Firebase session.', reason);
        });
      }
      freshPreviewSession = null;
      freshPreviewAdminUser = null;
      freshPreviewAccountId = null;
      freshAccountPreview = false;
      stoppingFreshAccountPreview = false;
      activeDb = db;
      user = auth.currentUser;
      isAdmin = primaryIsAdmin;
      sites = [];
      sitePageCursors = [null];
      sitePageIndex = 0;
      sitePageHasNext = false;
      siteListRequest++;
      statsRequestVersion++;
      statsLoading = false;
      termsAccepted = null;
      termsVersion = null;
      form = emptySiteForm();
      stats = null;
      statsSiteId = null;
      spendEstimates.clear();

      if (user) {
        try {
          const terms = await loadTermsAcceptance(user);
          termsAccepted = Boolean(
            terms.agreedToTermsAt && terms.termsVersion === terms.currentTermsVersion
          );
          termsVersion = terms.currentTermsVersion;
          if (termsAccepted) await refreshSites();
        } catch (reason) {
          showError(reason);
        }
      }

      navigate('/admin');
      requestRedraw();
    }
  }

  async function routeEvents(site, path) {
    try {
      return await loadRouteEvents(activeDb, site, path);
    } catch (reason) {
      console.error(`Unable to load route events for ${site.id}${path}.`, reason);
      showError(reason);
      throw reason;
    }
  }

  async function routeTimeline(site, path) {
    try {
      return await loadRouteTimeline(activeDb, site, path);
    } catch (reason) {
      console.error(`Unable to load route activity for ${site.id}${path}.`, reason);
      showError(reason);
      throw reason;
    }
  }

  async function routeEventPage(site, path, cursor) {
    try {
      return await loadRouteEventPage(activeDb, site, path, cursor);
    } catch (reason) {
      console.error(`Unable to load more route events for ${site.id}${path}.`, reason);
      showError(reason);
      throw reason;
    }
  }

  onAuthStateChanged(auth, async nextUser => {
    if (user?.uid !== nextUser?.uid) {
      siteListRequest += 1;
      siteListLoading = false;
      sitePageCursors = [null];
      sitePageIndex = 0;
      sitePageHasNext = false;
      sites = [];
      ownedSiteLoads.clear();
    }
    authResolved = false;
    user = nextUser;
    isAdmin = false;
    primaryIsAdmin = false;
    termsAccepted = null;
    termsVersion = null;

    if (user) {
      try {
        const token = await user.getIdTokenResult(true);
        isAdmin = token.claims.admin === true;
        primaryIsAdmin = isAdmin;
        const terms = await loadTermsAcceptance(user);
        termsAccepted = Boolean(
          terms.agreedToTermsAt && terms.termsVersion === terms.currentTermsVersion
        );
        termsVersion = terms.currentTermsVersion;

        if (termsAccepted) {
          await refreshSites();
        }
      } catch (reason) {
        termsAccepted = false;
        authResolved = true;
        showError(reason);
        return;
      }
    }

    authResolved = true;
    requestRedraw();
  });

  return {
    addDomain,
    accountBilling: targetUserId => loadAccountBilling(user, targetUserId),
    billingAccess: () => loadBillingAccess(user),
    updateAccountFreeTier: (targetUserId, mode, billingTestEnabled, freeTier) =>
      updateAccountFreeTier(user, targetUserId, mode, billingTestEnabled, freeTier),
    grantAccountDomainTrial: targetUserId => grantAccountDomainTrial(user, targetUserId),
    addFormCapture,
    addFormIgnoredRoute,
    clearRoute,
    changeForm,
    clearCache,
    createSiteCacheWebhook,
    form: () => form,
    canCreateSites: () => Boolean(user),
    newSite,
    prepareRoute,
    refreshDomain,
    removeConfiguredSite,
    removeFormIgnoredRoute,
    revokeSiteCacheWebhook,
    routeEvents,
    routeTimeline,
    routeEventPage,
    routeCacheInfo,
    routeScreenshot,
    signIn,
    siteListPage: () => sitePageIndex,
    siteListHasPrevious: () => sitePageIndex > 0,
    siteListHasNext: () => sitePageHasNext,
    siteListLoading: () => siteListLoading,
    changeSitePage,
    signOut: async () => {
      if (freshAccountPreview) {
        await stopFreshAccountPreview();
        return;
      }
      if (impersonationSession) {
        await stopImpersonation(impersonationSession);
        impersonationSession = null;
        impersonatedAccount = null;
        activeDb = db;
      }
      await signOut(auth);
    },
    impersonate: async targetUserId => {
      try {
        const result = await createImpersonationToken(user, targetUserId);
        impersonationSession = await startImpersonation(result.token);
        activeDb = impersonationSession.db;
        user = impersonationSession.user;
        isAdmin = false;
        impersonatedAccount = result.account;
        sites = [];
        sitePageCursors = [null];
        sitePageIndex = 0;
        sitePageHasNext = false;
        siteListRequest += 1;
        stats = null;
        statsSiteId = null;
        spendEstimates.clear();
        await refreshSites();
        navigate('/sites');
      } catch (reason) {
        showError(reason);
      }
    },
    stopImpersonation: async () => {
      if (!impersonationSession) return;
      await stopImpersonation(impersonationSession);
      impersonationSession = null;
      impersonatedAccount = null;
      activeDb = db;
      user = auth.currentUser;
      isAdmin = primaryIsAdmin;
      sites = [];
      sitePageCursors = [null];
      sitePageIndex = 0;
      sitePageHasNext = false;
      siteListRequest += 1;
      stats = null;
      statsSiteId = null;
      spendEstimates.clear();
      await refreshSites();
      navigate('/admin');
    },
    startFreshAccountPreview,
    stopFreshAccountPreview,
    save,
    saving: () => saving,
    spendEstimate,
    setStatsUsagePeriod,
    statsUsagePeriodHours: () => statsUsagePeriodHours,
    sites: () => sites,
    provisioningDomain: (site, domain) => provisioningDomains.has(domainKey(site.id, domain)),
    provisioningSite: site => [...provisioningDomains]
      .some(key => key.startsWith(`${site.id}:`)),
    removingSite: site => removingSiteId === site.id,
    removingDomain: (site, domain) => removingDomains.has(domainKey(site.id, domain)),
    removeConfiguredDomain,
    statsFor: site => statsSiteId === site.id ? stats : null,
    testConfiguredDomain,
    updateRouteSettings,
    updateRouteScreenshot,
    updateRouteRenderer,
    user: () => user,
    impersonating: () => Boolean(impersonationSession),
    impersonatedAccount: () => impersonatedAccount,
    isAdmin: () => isAdmin,
    freshAccountPreview: () => freshAccountPreview,
    startingFreshAccountPreview: () => startingFreshAccountPreview,
    stoppingFreshAccountPreview: () => stoppingFreshAccountPreview,
    authResolved: () => authResolved,
    termsAccepted: () => termsAccepted,
    cacheWebhookUrl: siteId => cacheWebhookUrls.get(siteId),
    acceptingTerms: () => acceptingTerms,
    acceptTerms,
    error: () => error,
    dismissError
  };
}

module.exports = { createAppController };
